import * as React from 'react';
import { createPortal } from 'react-dom';
import { DismissableLayer } from '@radix-ui/react-dismissable-layer';
import { FocusScope } from '@radix-ui/react-focus-scope';

import { cn } from './cn';
import { useUiPortalContainer } from '@/js/wisemetering/ui/render_with_ui_root';

// Canonical overlay primitive. Two shapes:
//
//   <Backdrop>                             // absolute scrim overlaying
//                                          // the closest positioned
//                                          // ancestor — soft dim
//   <Backdrop full onClose={fn}>           // viewport-fixed, strong dim,
//       {modalContent}                     // portals to ui-portal-root,
//   </Backdrop>                            // traps focus, listens for
//                                          // Escape, click-on-self → onClose
//
// `full` is the modal context — Dialog wraps this internally. Public
// surface keeps it exposed for one-off custom modals; reach for Dialog
// first.
//
// The dim look comes from the canonical `overlay-soft` / `overlay-strong`
// Tailwind tokens (defined in tailwind.config.js, alpha baked in). Soft
// is the decorative scrim; strong is the modal scrim. The intensity is
// implicit in `full` — if the future needs the other combinations, lift
// it back to a prop with a real callsite.

const Backdrop = React.forwardRef(({
    children,
    className: _className,
    full = false,
    onClose,
    style: _style,
    ...rest
}, ref) => {
    const portalContainer = useUiPortalContainer();

    const handleClick = full && onClose
        ? event => {
            if (event.target === event.currentTarget) {
                onClose();
            }
        }
        : undefined;

    const surface = (
        <div
            ref={ref}
            className={cn(
                'tw-inset-0',
                full
                    ? 'tailwind-container tw-fixed tw-z-modal tw-bg-overlay-strong'
                    : 'tw-absolute tw-bg-overlay-soft'
            )}
            onClick={handleClick}
            {...rest}
        >
            {children}
        </div>
    );

    if (!full) {
        return surface;
    }

    // `full` brings the whole modal stack: focus trap (loop + trapped),
    // DismissableLayer for Escape, click-on-self handled by the surface,
    // portal so the layer escapes its source position in the DOM.
    const content = (
        <FocusScope asChild loop trapped>
            <DismissableLayer asChild onEscapeKeyDown={onClose}>
                {surface}
            </DismissableLayer>
        </FocusScope>
    );

    return portalContainer ? createPortal(content, portalContainer) : content;
});

Backdrop.displayName = 'Backdrop';

export { Backdrop };
